import { Image, Page, Text, View } from '@react-pdf/renderer';
import React from 'react';
import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName, getScenarioStatusName } from 'src/api/types';
import { ScenarioPdfQuery } from 'src/graphql/_generated';
import { FooterStartComponent, Header } from './Header';
import { pdfStyles } from '../pdfStyles';
import { Footer } from './Footer';
import { formatDate, isUndefined } from 'src/utils/misc';
import { LegendExceedance } from './legends';
import { ControlsTable } from './ControlsTable';
import { IncidentsTable } from './IncidentsTable';
import { NoData } from './NoData';
import { EmptyChart } from './EmptyChart';
import { TitleLink } from './TitleLink';
import { ActionsTable, PdfLinkifyText, VariantValuesPDF } from 'src/components/pdf/comps';
import { ColorSet } from 'src/redux/reducers/theme';
import { ActionsConf, ScenarioPDF } from '../types';

type ScenarioGql = ScenarioPDF & { chartImage: string };

interface ScenarioPageProps {
  scenario: ScenarioGql;
  onRender: (pageNumber: number) => void;
  endComponent?: React.ReactNode;
  assessmentData: {
    reviewDate?: string;
    id: string;
  };
  footerTitle?: string;
  colors: ColorSet;
  includesActions: ActionsConf;
}

export const ScenarioPage: React.FC<ScenarioPageProps> = ({
  scenario,
  onRender,
  endComponent,
  assessmentData,
  footerTitle = '',
  colors,
  includesActions,
}) => {
  const isEmptyChart = scenario.liveRisk?.chart?.every((el) => el.y === 1 && el.x === 0);

  const showAllProjected = !!scenario.liveRisk?.expectedLoss;

  return (
    <>
      <Page size={'A4'} style={pdfStyles.page}>
        <Header endComponent={endComponent} colors={colors} />

        {/* @ts-ignore */}
        <View render={(data) => onRender(data.pageNumber)} />
        <View style={pdfStyles.section}>
          <TitleLink
            title={scenario.name || ''}
            intLinkId={(scenario.name || '').toLowerCase()}
            extLink={`risk-assessments/${assessmentData.id}/scenario/${scenario.id}`}
            colors={colors}
          />

          <View style={pdfStyles.spacer8} />
          <View style={pdfStyles.spacer4} />
          <View style={{ flexDirection: 'column', gap: 16 }}>
            <View style={{ flexDirection: 'column', marginBottom: 16 }}>
              <Text style={pdfStyles.label}>EXPECTED LOSS</Text>
              <View style={{ flexDirection: 'row', marginTop: 8 }}>
                <View style={{ marginRight: 15 }}>
                  <VariantValuesPDF value={{ base: scenario.baseRisk?.expectedLoss || undefined }} variant="scenario" />
                </View>
                <View style={{ marginRight: 15 }}>
                  <VariantValuesPDF value={{ live: scenario.liveRisk?.expectedLoss || undefined }} variant="scenario" />
                </View>
                <View>
                  <VariantValuesPDF
                    value={{ complete: scenario.projectedRisk?.expectedLoss || undefined }}
                    variant="scenario"
                  />
                </View>
              </View>
            </View>

            <View style={{ flexDirection: 'row', gap: 20 }}>
              <View style={{ flexDirection: 'column', width: 60 }}>
                <Text style={pdfStyles.label}>STATUS</Text>
                <View style={pdfStyles.spacer1} />
                <Text style={pdfStyles.value}>{getScenarioStatusName(scenario.status) || '-'}</Text>
              </View>
              <View style={{ width: 100 }}>
                <Text style={pdfStyles.label}>REVIEW DATE</Text>
                <View style={pdfStyles.spacer1} />
                <Text style={pdfStyles.value}>{formatDate(scenario.reviewDate, { emptyValue: '-' })}</Text>
              </View>
              <View style={{ width: 100 }}>
                <Text style={pdfStyles.label}>IDENTIFIED DATE</Text>
                <View style={pdfStyles.spacer1} />
                <Text style={pdfStyles.value}>{formatDate(scenario.riskIdentifiedDate, { emptyValue: '-' })}</Text>
              </View>
              <View style={{ width: 100 }}>
                <Text style={pdfStyles.label}>EDITED</Text>
                <View style={pdfStyles.spacer1} />
                <Text style={pdfStyles.value}>
                  {formatDate(scenario.editedAt, { emptyValue: '-', formatType: 'datetime' })}
                </Text>
              </View>
              <View style={{ width: 100 }}>
                <Text style={pdfStyles.label}>UPDATED</Text>
                <View style={pdfStyles.spacer1} />
                <Text style={pdfStyles.value}>
                  {formatDate(scenario.meta?.updatedAt, { emptyValue: '-', formatType: 'datetime' })}
                </Text>
              </View>
            </View>
          </View>

          <View style={pdfStyles.spacer16} />
          <View style={pdfStyles.spacer8} />

          <>
            <Text style={pdfStyles.gradText}>RISK STATEMENT</Text>
            <View style={pdfStyles.spacer8} />
            <View style={{ flexDirection: 'row' }}>
              <View style={{ width: 220 }}>
                <Text style={pdfStyles.label}>SOURCES</Text>
                <View style={pdfStyles.spacer1} />
                {!isUndefined(scenario.source) ? (
                  <Text style={pdfStyles.value}>{getRiskSourcesName(scenario.source)}</Text>
                ) : (
                  <NoData colors={colors} />
                )}
              </View>
              <View>
                <Text style={pdfStyles.label}>EVENTS</Text>
                <View style={pdfStyles.spacer1} />
                {!isUndefined(scenario.event) ? (
                  <Text style={pdfStyles.value}>{getRiskEventsName(scenario.event)}</Text>
                ) : (
                  <NoData colors={colors} />
                )}
              </View>
            </View>
            <View style={pdfStyles.spacer8} />
            <View style={{ flexDirection: 'row' }}>
              <View style={{ width: 220 }}>
                <Text style={pdfStyles.label}>CONSEQUENCES</Text>
                <View style={pdfStyles.spacer1} />
                {!isUndefined(scenario.consequence) ? (
                  <Text style={pdfStyles.value}>{getRiskConsequencesName(scenario.consequence)}</Text>
                ) : (
                  <NoData colors={colors} />
                )}
              </View>
              <View>
                <Text style={pdfStyles.label}>RISK OWNER</Text>
                <View style={pdfStyles.spacer1} />
                {scenario.riskOwner ? (
                  <Text style={pdfStyles.value}>
                    {(scenario.riskOwner.text || scenario.riskOwner.email || '-').split('')}
                  </Text>
                ) : (
                  <NoData colors={colors} />
                )}
              </View>
            </View>
          </>

          <View style={pdfStyles.spacer16} />
          <View style={pdfStyles.spacer8} />

          <>
            <Text style={pdfStyles.gradText}>LOSS EXCEEDANCE CURVE</Text>
            <View style={pdfStyles.spacer4} />
            {!isEmptyChart ? (
              <>
                <Image src={scenario.chartImage} style={pdfStyles.chartImage} />
                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: -8 }}>
                  <LegendExceedance colors={colors} showAllProjected={showAllProjected} />
                </View>
              </>
            ) : (
              <EmptyChart colors={colors} />
            )}
          </>

          <View style={pdfStyles.spacer8} />

          <>
            <View wrap={false}>
              <Text style={pdfStyles.label}>RISK NARRATIVE</Text>
              <View style={pdfStyles.spacer1} />
              {scenario.riskNarrative ? (
                <PdfLinkifyText>{scenario.riskNarrative}</PdfLinkifyText>
              ) : (
                <NoData colors={colors} />
              )}
            </View>
            <View style={pdfStyles.spacer8} />
            <View wrap={false}>
              <Text style={pdfStyles.label}>FREQUENCY</Text>
              <View style={pdfStyles.spacer1} />
              {!(!scenario.baseRisk?.frequencyTimes && !scenario.baseRisk?.frequencyYears) ? (
                <View style={{ flexDirection: 'column', gap: 4 }}>
                  {scenario.baseRisk?.frequencyTimes && scenario.baseRisk?.frequencyYears && (
                    <>
                      {scenario.baseRisk?.frequencyTimes && scenario.baseRisk?.frequencyYears && (
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                          <View
                            style={{
                              width: 7,
                              height: 7,
                              backgroundColor: '#022033',
                              borderRadius: '50%',
                              marginRight: 6,
                            }}
                          />
                          <Text style={pdfStyles.value}>
                            {scenario.baseRisk.frequencyTimes} time(s) per {scenario.baseRisk.frequencyYears} year(s)
                          </Text>
                        </View>
                      )}
                      {scenario.liveRisk?.frequencyTimes && scenario.liveRisk?.frequencyYears && (
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                          <View
                            style={{
                              width: 7,
                              height: 7,
                              backgroundColor: '#E62E78',
                              borderRadius: '50%',
                              marginRight: 6,
                            }}
                          />
                          <Text style={pdfStyles.value}>
                            {scenario.liveRisk.frequencyTimes} time(s) per {scenario.liveRisk.frequencyYears} year(s)
                          </Text>
                        </View>
                      )}
                      {scenario.projectedRisk?.frequencyTimes && scenario.projectedRisk?.frequencyYears && (
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                          <View
                            style={{
                              width: 7,
                              height: 7,
                              backgroundColor: '#2BBDEF',
                              borderRadius: '50%',
                              marginRight: 6,
                            }}
                          />
                          <Text style={pdfStyles.value}>
                            {scenario.projectedRisk.frequencyTimes} time(s) per {scenario.projectedRisk.frequencyYears}{' '}
                            year(s)
                          </Text>
                        </View>
                      )}
                    </>
                  )}
                </View>
              ) : (
                <NoData colors={colors} />
              )}
            </View>

            <View style={pdfStyles.spacer8} />
            <View wrap={false}>
              <Text style={pdfStyles.label}>NOTE</Text>
              <View style={pdfStyles.spacer1} />
              {scenario.frequencyNote ? (
                <PdfLinkifyText>{scenario.frequencyNote}</PdfLinkifyText>
              ) : (
                <NoData colors={colors} />
              )}
            </View>
          </>

          <View style={pdfStyles.spacer16} />
          <View style={pdfStyles.spacer8} />

          <>
            <View wrap={false}>
              <Text style={pdfStyles.label}>CONSEQUENCE FACTORS</Text>
              <View style={pdfStyles.spacer8} />
              <View style={{ flexDirection: 'row', gap: 40, marginBottom: 16 }}>
                <View style={{ width: 100 }}>
                  <Text style={pdfStyles.label}>LOWER</Text>
                  <View style={{ marginTop: 8 }}>
                    {scenario.baseRisk?.lower != null && (
                      <VariantValuesPDF value={{ base: scenario.baseRisk.lower }} variant="scenario" />
                    )}
                    {scenario.liveRisk?.lower != null && (
                      <VariantValuesPDF value={{ live: scenario.liveRisk.lower }} variant="scenario" />
                    )}
                    {scenario.projectedRisk?.lower != null && (
                      <VariantValuesPDF value={{ complete: scenario.projectedRisk.lower }} variant="scenario" />
                    )}
                  </View>
                </View>

                <View style={{ width: 100 }}>
                  <Text style={pdfStyles.label}>UPPER</Text>
                  <View style={{ marginTop: 8 }}>
                    {scenario.baseRisk?.upper != null && (
                      <VariantValuesPDF value={{ base: scenario.baseRisk.upper }} variant="scenario" />
                    )}
                    {scenario.liveRisk?.upper != null && (
                      <VariantValuesPDF value={{ live: scenario.liveRisk.upper }} variant="scenario" />
                    )}
                    {scenario.projectedRisk?.upper != null && (
                      <VariantValuesPDF value={{ complete: scenario.projectedRisk.upper }} variant="scenario" />
                    )}
                  </View>
                </View>
              </View>
            </View>
            <View style={pdfStyles.spacer8} />
            <View wrap={false}>
              <Text style={pdfStyles.label}>NOTE</Text>
              <View style={pdfStyles.spacer1} />
              {scenario.consequenceNote ? (
                <PdfLinkifyText>{scenario.consequenceNote}</PdfLinkifyText>
              ) : (
                <NoData colors={colors} />
              )}
            </View>
          </>

          <View style={pdfStyles.spacer16} />
          <View style={pdfStyles.spacer8} />

          <View wrap={false}>
            <Text style={pdfStyles.gradText}>RISK MANAGEMENT</Text>
            <View style={pdfStyles.spacer8} />
            <Text style={pdfStyles.label}>MANAGEMENT STRATEGY</Text>
            <View style={pdfStyles.spacer1} />
            {scenario.managementStrategy ? (
              <PdfLinkifyText>{scenario.managementStrategy}</PdfLinkifyText>
            ) : (
              <NoData colors={colors} />
            )}
            <View style={pdfStyles.spacer8} />
            <Text style={pdfStyles.label}>NOTE</Text>
            <View style={pdfStyles.spacer1} />
            {scenario.managementNote ? (
              <PdfLinkifyText>{scenario.managementNote}</PdfLinkifyText>
            ) : (
              <NoData colors={colors} />
            )}
          </View>

          <>
            <View style={pdfStyles.spacer16} />
            <View style={pdfStyles.spacer8} />
            <Text style={pdfStyles.gradText}>RISK MANAGEMENT PLAN FOR THIS SCENARIO</Text>
            <View style={pdfStyles.spacer8} />
            {scenario.riskManagementPlans?.length ? (
              <ControlsTable
                data={scenario.riskManagementPlans.map((rmp) => rmp.control).filter(Boolean)}
                colors={colors}
                scenarioId={scenario.id}
              />
            ) : (
              <NoData colors={colors} />
            )}
          </>

          <ActionsTable data={scenario.actions} colors={colors} includesActions={includesActions} />

          <>
            <View style={pdfStyles.spacer16} />
            <View style={pdfStyles.spacer8} />
            <Text style={pdfStyles.gradText}>INCIDENTS</Text>
            <View style={pdfStyles.spacer8} />
            {scenario.incidents?.length ? (
              <IncidentsTable data={scenario.incidents} colors={colors} />
            ) : (
              <NoData colors={colors} />
            )}
          </>
        </View>
        <Footer footerComponent={<FooterStartComponent title={`${footerTitle}${scenario.name}`} />} colors={colors} />
      </Page>
    </>
  );
};
